import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, useMediaQuery, Avatar } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { ReturnModal } from '@components/modals/ReturnModal'
import { emotionTheme } from '@styles/theme'
import { Select } from '@components/shared/Select'
import { DatePicker } from '@components/shared/DatePicker'
import { Input } from '@components/shared/Input'
import { InfoIcon } from '@assets/svgs/InfoIcon'
import { MoneyIcon } from '@assets/svgs/MoneyIcon'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { PencilSimpleIcon, UserMinus, CalculatorIcon, CheckCircleIcon, MinusCircleIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { Tooltip } from '@components/shared/Tooltip'
import { currencyMask } from '@utils/masks/currencyMask'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { formattedDatePtBr } from '@utils/dates/formattedDatePtBr'
import { useUser } from '@hooks/useUser'
import { usePeople } from '@pages/People/context'
import * as apiPeople from '@services/api/people'

import type { RescissionSimulationModalProps } from './types'
import { createRescissionSimulationSchema } from './form/validations'
import { initialFormValues } from './form/initialFormValues'
import type { RescissionSimulationFormProps } from './form/types'
import type { RescissionSimulationRequestProps, CollaboratorDetailsResponseProps } from '@pages/People/context/types'
import { calculateFinancialSummary } from './helpers/calculations'
import { convertDateToIso, convertDateToIsoDate, formatDateForDisplay, parseIsoDateAsLocal, parseIsoDateAsLocalEndOfDay } from './helpers/dateConverter'
import { EditFgtsModal } from './components/EditFgtsModal'
import * as S from './styles'
import { BankIcon } from '@assets/svgs/BankIcon'
import { AccountBalanceWallet } from '@assets/svgs/AccountBalanceWallet'

// Função auxiliar para converter salário editável para número
const convertSalaryToNumber = (salarioEditavel: string | undefined, defaultSalary: number): number => {
  if (!salarioEditavel || salarioEditavel.trim() === '') {
    return defaultSalary
  }

  const trimmed = salarioEditavel.trim()

  // Se já for um número válido (string numérica simples), converter diretamente
  if (/^\d+([.,]\d+)?$/.test(trimmed)) {
    const numero = Number(trimmed.replace(',', '.'))
    if (!isNaN(numero) && numero > 0 && numero < 10000000) { // Limite de segurança: 10 milhões
      return numero
    }
  }

  // Tentar unMaskCurrency (para valores formatados como "R$ 1.000,00")
  const unmasked = unMaskCurrency(trimmed)
  if (unmasked > 0 && unmasked < 10000000) { // Limite de segurança: 10 milhões
    return unmasked
  }

  // Se unMaskCurrency retornou 0 mas o valor não é "0", tentar conversão direta
  if (trimmed !== '0') {
    // Remover caracteres não numéricos exceto vírgula e ponto
    const cleaned = trimmed.replace(/[^\d.,]/g, '')
    if (cleaned) {
      // Substituir vírgula por ponto para conversão
      const withDot = cleaned.replace(',', '.')
      const directNumber = Number(withDot)

      if (!isNaN(directNumber) && directNumber > 0 && directNumber < 10000000) {
        return directNumber
      }
    }
  }

  // Se tudo falhar, retornar o salário padrão
  return defaultSalary
}

const resolveManualFgtsArt22 = (fgtsEditavel: string | undefined, fgtsFromApi: number | null): number | null => {
  const editedFgts = fgtsEditavel?.trim()
    ? convertSalaryToNumber(fgtsEditavel, 0)
    : 0

  return editedFgts > 0 ? editedFgts : fgtsFromApi
}

export const RescissionSimulationModal = ({
  open,
  onClose,
  collaborator,
  onConfirm,
  isLoading = false,
  simulationToEdit = null,
  isEditMode = false,
  canEdit = true
}: RescissionSimulationModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { filiData } = useUser()
  const { createRescissionSimulation, isRescissionSimulationLoading, editRescissionSimulation } = usePeople()

  const [proportionalSalaryFromApi, setProportionalSalaryFromApi] = useState<number | null>(null)
  const [isCalculatingSalary, setIsCalculatingSalary] = useState(false)
  const [fgtsFromApi, setFgtsFromApi] = useState<number | null>(null)
  const [completeMonthsFromApi, setCompleteMonthsFromApi] = useState<number | null>(null)
  const [isCalculatingFgts, setIsCalculatingFgts] = useState(false)
  const [noticePaymentFromApi, setNoticePaymentFromApi] = useState<number | null>(null)
  const [isCalculatingNoticePayment, setIsCalculatingNoticePayment] = useState(false)
  const [totalEstimatedFromApi, setTotalEstimatedFromApi] = useState<number | null>(null)
  const [balanceFromApi, setBalanceFromApi] = useState<number | null>(null)
  const [salariesFromApi, setSalariesFromApi] = useState<number | null>(null)
  const [isCalculatingTotalEstimated, setIsCalculatingTotalEstimated] = useState(false)
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [isCancelModalConfirming, setIsCancelModalConfirming] = useState(false)
  const cancelConfirmLockRef = useRef(false)

  useEffect(() => {
    if (openCancelModal) {
      cancelConfirmLockRef.current = false
      setIsCancelModalConfirming(false)
    }
  }, [openCancelModal])

  const [openEditFgtsModal, setOpenEditFgtsModal] = useState(false)
  const [collaboratorDetails, setCollaboratorDetails] = useState<CollaboratorDetailsResponseProps | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)

  // Salário "Salários e Ordenados" vindo da API de detalhes (usado como base para simulação)
  const salaryFromDetails = useMemo(() => {
    if (!collaboratorDetails?.salaries?.items?.length) return null
    const item = collaboratorDetails.salaries.items.find(
      (i) => i.name === 'Salários e Ordenados'
    )
    return item != null ? item.value : null
  }, [collaboratorDetails])

  // Buscar detalhes do colaborador ao abrir o modal
  useEffect(() => {
    const employeeRegistration = isEditMode && simulationToEdit
      ? simulationToEdit.employeeRegistration
      : collaborator?.matricula

    if (!open || !employeeRegistration || !filiData?.filiCode || !filiData?.startPeriod) {
      setCollaboratorDetails(null)
      return
    }
    const referenceDate =
      filiData.startPeriod.match(/^\d{4}-\d{2}-\d{2}$/)
        ? filiData.startPeriod
        : convertDateToIsoDate(filiData.startPeriod) ?? filiData.startPeriod

    setIsLoadingDetails(true)
    apiPeople
      .getCollaboratorDetails({
        filiCode: filiData.filiCode,
        employeeRegistration,
        referenceDate
      })
      .then((data) => setCollaboratorDetails(data ?? null))
      .catch(() => setCollaboratorDetails(null))
      .finally(() => setIsLoadingDetails(false))
  }, [open, isEditMode, collaborator?.matricula, simulationToEdit?.employeeRegistration, filiData?.filiCode, filiData?.startPeriod])

  const validationSchema = useMemo(() => {
    if (!filiData?.startPeriod || !filiData?.endPeriod) {
      return null
    }
    return createRescissionSimulationSchema(filiData.startPeriod, filiData.endPeriod)
  }, [filiData?.startPeriod, filiData?.endPeriod])

  const methods = useForm<RescissionSimulationFormProps>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: (validationSchema || undefined) as any,
    defaultValues: initialFormValues
  })

  // Watchers para reatividade (RF11)
  const tipoAviso = methods.watch('tipoAviso')
  const dataRescisao = methods.watch('dataRescisao')
  const salarioEditavel = methods.watch('salarioEditavel')
  const fgtsEditavel = methods.watch('fgtsEditavel')

  // Verificar se há alterações não salvas
  const { isDirty } = methods.formState

  // Salário base: salário da simulação (edição) > detalhes (Salários e Ordenados) >
  // colaborador da listagem > 0. Simulações antigas voltam com salary = 0, por isso o fallback.
  const effectiveSalary = isEditMode && simulationToEdit && simulationToEdit.salary > 0
    ? simulationToEdit.salary
    : (salaryFromDetails ?? collaborator?.salario ?? 0)

  // Calcular salário proporcional via API quando salário ou data mudarem
  useEffect(() => {
    const defaultSalary = effectiveSalary

    if ((!collaborator && !isEditMode) || !dataRescisao || !filiData?.filiCode) {
      setProportionalSalaryFromApi(null)
      return
    }

    // Converter e validar salário
    let salario = convertSalaryToNumber(salarioEditavel, defaultSalary)

    // Validação de segurança: garantir que o salário não seja maior que 10 milhões
    if (salario > 10000000 || salario <= 0 || isNaN(salario)) {
      console.warn('Salário inválido detectado:', { salario, salarioEditavel, defaultSalary })
      salario = defaultSalary > 0 && defaultSalary < 10000000
        ? defaultSalary
        : 0
    }

    if (!salario || salario <= 0) {
      setProportionalSalaryFromApi(null)
      return
    }

    // Converter data de rescisão para ISO
    const startDate = convertDateToIso(dataRescisao)
    if (!startDate) {
      setProportionalSalaryFromApi(null)
      return
    }

    setIsCalculatingSalary(true)

    apiPeople.calculateProportionalSalaryRescission(filiData.filiCode, {
      salary: salario,
      terminationDate: startDate
    })
      .then((response) => {
        setProportionalSalaryFromApi(response.proportionalSalary)
      })
      .catch((error) => {
        console.error('Erro ao calcular salário proporcional:', error)
        setProportionalSalaryFromApi(null)
      })
      .finally(() => {
        setIsCalculatingSalary(false)
      })
  }, [dataRescisao, salarioEditavel, effectiveSalary, isEditMode, simulationToEdit, filiData?.filiCode])

  // Calcular FGTS Art. 22 via API quando salário, data início, data rescisão ou tipo aviso mudarem
  useEffect(() => {
    const defaultSalary = effectiveSalary
    const dataInicio = isEditMode && simulationToEdit?.startDate
      ? simulationToEdit.startDate
      : collaborator?.dataInicio || ''

    if ((!collaborator && !isEditMode) || !tipoAviso || !dataRescisao || !dataInicio) {
      setFgtsFromApi(null)
      return
    }

    const salario = convertSalaryToNumber(salarioEditavel, defaultSalary)
    if (salario <= 0 || salario > 10000000 || isNaN(salario)) {
      setFgtsFromApi(null)
      return
    }

    const startDate = dataInicio.match(/^\d{4}-\d{2}-\d{2}/)
      ? dataInicio.substring(0, 10)
      : convertDateToIsoDate(dataInicio)
    const terminationDate = dataRescisao.match(/^\d{4}-\d{2}-\d{2}/)
      ? dataRescisao.substring(0, 10)
      : convertDateToIsoDate(dataRescisao)
    if (!startDate || !terminationDate) {
      setFgtsFromApi(null)
      return
    }

    const noticeType = tipoAviso === 'TRABALHADO' ? 'TRABALHADO' : 'INDENIZADO'
    setIsCalculatingFgts(true)
    apiPeople.calculateFgtsArt22({
      salary: salario,
      startDate,
      terminationDate,
      noticeType
    })
      .then((response) => {
        setFgtsFromApi(response.fgtsAmount)
        setCompleteMonthsFromApi(response.completeMonths)
      })
      .catch((error) => {
        console.error('Erro ao calcular FGTS Art. 22:', error)
        setFgtsFromApi(null)
        setCompleteMonthsFromApi(null)
      })
      .finally(() => {
        setIsCalculatingFgts(false)
      })
  }, [tipoAviso, dataRescisao, salarioEditavel, effectiveSalary, collaborator, isEditMode, simulationToEdit])

  // Calcular aviso prévio via API quando salário, data início, data rescisão ou tipo aviso mudarem
  useEffect(() => {
    const defaultSalary = effectiveSalary
    const dataInicio = isEditMode && simulationToEdit?.startDate
      ? simulationToEdit.startDate
      : collaborator?.dataInicio || ''

    if ((!collaborator && !isEditMode) || !tipoAviso || !dataRescisao || !dataInicio) {
      setNoticePaymentFromApi(null)
      return
    }

    const salario = convertSalaryToNumber(salarioEditavel, defaultSalary)
    if (salario <= 0 || salario > 10000000 || isNaN(salario)) {
      setNoticePaymentFromApi(0)
      return
    }

    const admissionDate = dataInicio.match(/^\d{4}-\d{2}-\d{2}/)
      ? dataInicio.substring(0, 10)
      : convertDateToIsoDate(dataInicio)
    const terminationDate = dataRescisao.match(/^\d{4}-\d{2}-\d{2}/)
      ? dataRescisao.substring(0, 10)
      : convertDateToIsoDate(dataRescisao)
    if (!admissionDate || !terminationDate) {
      setNoticePaymentFromApi(0)
      return
    }

    const noticeType = tipoAviso === 'TRABALHADO' ? 'TRABALHADO' : 'INDENIZADO'
    setIsCalculatingNoticePayment(true)
    apiPeople.calculateNoticePeriod({
      salary: salario,
      admissionDate,
      terminationDate,
      noticeType
    })
      .then((response) => {
        const noticePayment = Number(response.noticePayment)
        setNoticePaymentFromApi(Number.isFinite(noticePayment) ? noticePayment : 0)
      })
      .catch((error) => {
        console.error('Erro ao calcular aviso prévio:', error)
        setNoticePaymentFromApi(0)
      })
      .finally(() => {
        setIsCalculatingNoticePayment(false)
      })
  }, [tipoAviso, dataRescisao, salarioEditavel, effectiveSalary, collaborator, isEditMode, simulationToEdit])

  // Calcular total estimado via API quando tipo aviso, data rescisão ou FGTS mudarem
  useEffect(() => {
    const employeeRegistration = isEditMode && simulationToEdit
      ? simulationToEdit.employeeRegistration
      : collaborator?.matricula
    if (!filiData?.filiCode || !filiData?.startPeriod || !employeeRegistration || !tipoAviso || !dataRescisao) {
      setTotalEstimatedFromApi(null)
      setBalanceFromApi(null)
      setSalariesFromApi(null)
      return
    }

    const terminationDateIso = convertDateToIso(dataRescisao)
    if (!terminationDateIso) {
      setTotalEstimatedFromApi(null)
      setBalanceFromApi(null)
      setSalariesFromApi(null)
      return
    }

    let referenceDate = filiData.startPeriod
    if (referenceDate && !referenceDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      referenceDate = convertDateToIsoDate(referenceDate) || referenceDate
    }

    const noticeType = tipoAviso === 'TRABALHADO' ? 'TRABALHADO' : 'INDENIZADO'
    const manualFgts = resolveManualFgtsArt22(fgtsEditavel, fgtsFromApi)
    if (manualFgts === null) {
      setTotalEstimatedFromApi(null)
      setBalanceFromApi(null)
      setSalariesFromApi(null)
      return
    }

    const salario = convertSalaryToNumber(salarioEditavel, effectiveSalary)
    if (salario <= 0 || salario > 10000000 || isNaN(salario)) {
      setTotalEstimatedFromApi(null)
      setBalanceFromApi(null)
      setSalariesFromApi(null)
      return
    }

    setIsCalculatingTotalEstimated(true)
    apiPeople.calculateRescissionTotalEstimated({
      filiCode: filiData.filiCode,
      referenceDate,
      employeeRegistration,
      noticeType,
      terminationDate: terminationDateIso,
      salary: salario,
      manualFgtsArt22: manualFgts
    })
      .then((response) => {
        setTotalEstimatedFromApi(response.totalEstimated)
        setBalanceFromApi(response.balance ?? null)
        setSalariesFromApi(response.salaries ?? null)
      })
      .catch((error) => {
        console.error('Erro ao calcular total estimado:', error)
        setTotalEstimatedFromApi(null)
        setBalanceFromApi(null)
        setSalariesFromApi(null)
      })
      .finally(() => {
        setIsCalculatingTotalEstimated(false)
      })
  }, [tipoAviso, dataRescisao, salarioEditavel, effectiveSalary, fgtsEditavel, fgtsFromApi, filiData?.filiCode, filiData?.startPeriod, collaborator?.matricula, isEditMode, simulationToEdit])

  // Calcular resumo financeiro em tempo real
  const financialSummary = useMemo(() => {
    const defaultSalary = effectiveSalary
    const dataInicio = isEditMode && simulationToEdit?.startDate
      ? simulationToEdit.startDate
      : collaborator?.dataInicio || ''

    if ((!collaborator && !isEditMode) || !tipoAviso || !dataRescisao) {
      return {
        salarioProporcional: 0,
        avisoPrevio: 0,
        fgts: 0,
        decimoTerceiroProporcional: 0,
        feriasProporcionais: 0,
        total: 0
      }
    }

    // Converter e validar salário
    let salario = convertSalaryToNumber(salarioEditavel, defaultSalary)

    // Validação de segurança: garantir que o salário não seja maior que 10 milhões
    if (salario > 10000000 || salario <= 0 || isNaN(salario)) {
      console.warn('Salário inválido detectado:', { salario, salarioEditavel, defaultSalary })
      salario = defaultSalary > 0 && defaultSalary < 10000000
        ? defaultSalary
        : 0
    }

    // Usar salário proporcional da API se disponível, senão calcular localmente
    const salarioProporcional = proportionalSalaryFromApi !== null
      ? proportionalSalaryFromApi
      : calculateFinancialSummary({
        salario,
        dataInicio,
        dataRescisao,
        tipoAviso: tipoAviso as 'TRABALHADO' | 'INDENIZADO',
        semJustaCausa: true
      }).salarioProporcional

    // Calcular outros valores (aviso prévio, FGTS, etc.)
    const summary = calculateFinancialSummary({
      salario,
      dataInicio,
      dataRescisao,
      tipoAviso: tipoAviso as 'TRABALHADO' | 'INDENIZADO',
      semJustaCausa: true
    })

    // FGTS: usar valor da API quando disponível, senão fallback do cálculo local
    const fgtsBase = fgtsFromApi !== null ? fgtsFromApi : summary.fgts

    // Substituir salário proporcional e FGTS pelos valores da API
    const finalSummary = {
      ...summary,
      salarioProporcional,
      avisoPrevio: noticePaymentFromApi ?? 0,
      fgts: fgtsBase
    }

    // Se FGTS foi editado, usar o valor editado
    if (fgtsEditavel && !isNaN(Number(fgtsEditavel))) {
      const fgtsEditado = Number(fgtsEditavel)
      const diferenca = fgtsEditado - finalSummary.fgts
      const novoTotal = finalSummary.total + diferenca
      return {
        salarioProporcional: Math.round(finalSummary.salarioProporcional * 100) / 100,
        avisoPrevio: Math.round(finalSummary.avisoPrevio * 100) / 100,
        fgts: Math.round(fgtsEditado * 100) / 100,
        decimoTerceiroProporcional: Math.round(finalSummary.decimoTerceiroProporcional * 100) / 100,
        feriasProporcionais: Math.round(finalSummary.feriasProporcionais * 100) / 100,
        total: Math.round(novoTotal * 100) / 100
      }
    }

    // Recalcular total com salário proporcional da API
    const total = finalSummary.salarioProporcional +
      finalSummary.avisoPrevio +
      finalSummary.fgts +
      finalSummary.decimoTerceiroProporcional +
      finalSummary.feriasProporcionais

    // Arredondar todos os valores para 2 casas decimais
    return {
      salarioProporcional: Math.round(finalSummary.salarioProporcional * 100) / 100,
      avisoPrevio: Math.round(finalSummary.avisoPrevio * 100) / 100,
      fgts: Math.round(finalSummary.fgts * 100) / 100,
      decimoTerceiroProporcional: Math.round(finalSummary.decimoTerceiroProporcional * 100) / 100,
      feriasProporcionais: Math.round(finalSummary.feriasProporcionais * 100) / 100,
      total: Math.round(total * 100) / 100
    }
  }, [tipoAviso, dataRescisao, salarioEditavel, fgtsEditavel, effectiveSalary, collaborator, proportionalSalaryFromApi, fgtsFromApi, noticePaymentFromApi, isEditMode, simulationToEdit])

  // Resetar formulário quando modal fechar ou colaborador mudar
  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
      setTotalEstimatedFromApi(null)
      setBalanceFromApi(null)
      setSalariesFromApi(null)
      setFgtsFromApi(null)
      setNoticePaymentFromApi(null)
      setCollaboratorDetails(null)
      return
    }

    // Modo de edição: pré-preencher com dados da simulação
    if (open && isEditMode && simulationToEdit) {
      // API retorna terminationDate em ISO (ex: "2026-02-20T00:00:00"); DatePicker espera dd/MM/yyyy
      const dataRescisaoEdit = simulationToEdit.terminationDate
        ? formatDateForDisplay(simulationToEdit.terminationDate) || ''
        : ''

      methods.reset({
        tipoAviso: (simulationToEdit.noticeType as 'TRABALHADO' | 'INDENIZADO') || '',
        dataRescisao: dataRescisaoEdit,
        semJustaCausa: simulationToEdit.terminationType === 'Sem Justa Causa',
        salarioEditavel: effectiveSalary > 0 ? effectiveSalary.toFixed(2) : '',
        fgtsEditavel: simulationToEdit.fgtsArt22 ? String(simulationToEdit.fgtsArt22) : ''
      })
      return
    }

    // Modo de criação: usar dados do colaborador (salário será atualizado quando detalhes carregarem)
    if (open && collaborator) {
      const salarioInicial = salaryFromDetails ?? collaborator.salario ?? 0
      methods.reset({
        ...initialFormValues,
        semJustaCausa: true,
        salarioEditavel: salarioInicial > 0 ? salarioInicial.toFixed(2) : '',
        fgtsEditavel: ''
      })
    }
  }, [open, collaborator, simulationToEdit, isEditMode, methods, salaryFromDetails, effectiveSalary])

  const onSubmit = async () => {
    if (!canEdit) return

    const formData = methods.getValues()

    if (!formData.tipoAviso || !formData.dataRescisao || !filiData?.filiCode || !filiData?.startPeriod) {
      return
    }

    try {
      // Converter noticeType para o formato da API
      const noticeType = formData.tipoAviso === 'TRABALHADO' ? 'TRABALHADO' : 'INDENIZADO'

      // Converter data de rescisão para formato yyyy-MM-dd
      const terminationDate = convertDateToIsoDate(formData.dataRescisao)
      if (!terminationDate) {
        return
      }

      // Converter referenceDate para formato yyyy-MM-dd (query param)
      // Se já estiver no formato correto, usar diretamente, senão converter
      let referenceDate = filiData.startPeriod
      if (referenceDate && !referenceDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Se não estiver no formato yyyy-MM-dd, tentar converter
        referenceDate = convertDateToIsoDate(referenceDate) || referenceDate
      }

      // Converter FGTS editável para número
      const fgtsValue = resolveManualFgtsArt22(fgtsEditavel, fgtsFromApi) ?? undefined

      // Salário considerado na simulação (respeita o valor editado no formulário)
      const salaryValue = convertSalaryToNumber(salarioEditavel, effectiveSalary)

      // Modo de edição
      if (isEditMode && simulationToEdit) {
        await editRescissionSimulation(
          filiData.filiCode,
          simulationToEdit.id,
          {
            employeeRegistration: simulationToEdit.employeeRegistration,
            noticeType,
            terminationDate,
            manualFgtsArt22: fgtsValue,
            balance: saldoRescisao
          },
          referenceDate
        )
      } else {
        // Modo de criação
        const createPayload: RescissionSimulationRequestProps = {
          employeeRegistration: collaborator?.matricula || 'TEMP',
          noticeType,
          terminationDate,
          salary: salaryValue,
          manualFgtsArt22: fgtsValue,
          balance: saldoRescisao,
          referenceDate
        }

        await createRescissionSimulation(filiData.filiCode, createPayload)
      }

      // Chamar callback com dados do formulário
      onConfirm({
        tipoAviso: formData.tipoAviso as 'TRABALHADO' | 'INDENIZADO',
        dataRescisao: formData.dataRescisao,
        semJustaCausa: formData.semJustaCausa
      })

      // Resetar formulário
      methods.reset(initialFormValues)

      // Fechar modal após sucesso
      onClose(false)
    } catch (error) {
      // Erro já foi tratado no provider com toast
      console.error('Erro ao salvar simulação:', error)
    }
  }

  // Interceptar fechamento do modal para verificar alterações não salvas (RF14)
  const handleClose = (value: boolean) => {
    if (isDirty && !value) {
      // Se há alterações não salvas, mostrar modal de confirmação
      setOpenCancelModal(true)
    } else {
      methods.reset(initialFormValues)
      onClose(value)
    }
  }

  const handleOpenEditFgtsModal = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!canEdit) return
    setOpenEditFgtsModal(true)
  }

  // Em modo de edição, não precisa de collaborator
  if (!isEditMode && !collaborator) return null

  const minDate = filiData?.startPeriod
    ? (filiData.startPeriod.match(/^\d{4}-\d{2}-\d{2}/)
      ? parseIsoDateAsLocal(filiData.startPeriod)
      : new Date(filiData.startPeriod))
    : new Date(1900, 0, 1)

  const maxDate = filiData?.endPeriod
    ? (filiData.endPeriod.match(/^\d{4}-\d{2}-\d{2}/)
      ? parseIsoDateAsLocalEndOfDay(filiData.endPeriod)
      : new Date(filiData.endPeriod))
    : new Date(2100, 11, 31)

  const estimatedTotal = totalEstimatedFromApi ??
    financialSummary.salarioProporcional +
    financialSummary.fgts +
    financialSummary.avisoPrevio

  // Saldo vem da API (`balance` de calcular-total-estimado); fallback local enquanto não responder
  const saldoRescisao = balanceFromApi ?? (estimatedTotal - financialSummary.salarioProporcional)

  // "Salários e Ordenados" vem da API (`salaries`); fallback local enquanto não responder
  const salariosOrdenados = salariesFromApi ?? financialSummary.salarioProporcional

  return (
    <>
      <BaseModal2
        open={open}
        title=""
        onClose={handleClose}
        width={isMobile ? 'calc(100% - 20px)' : 940}
        heightContainer="auto"
        maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: canEdit ? 'Confirmar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: canEdit ? () => methods.handleSubmit(onSubmit)() : undefined,
          loading: isLoading || isRescissionSimulationLoading
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <S.FormContainer>
              {/* Breadcrumb */}
              <S.Breadcrumb>
                <S.BreadcrumbItem>Pessoal</S.BreadcrumbItem>
                <S.BreadcrumbSeparator>&gt;</S.BreadcrumbSeparator>
                <S.BreadcrumbItem>Gestão de Colaboradores</S.BreadcrumbItem>
                <S.BreadcrumbSeparator>&gt;</S.BreadcrumbSeparator>
                <S.BreadcrumbItemBold>
                  Simulações - {isEditMode ? 'Editar Simulação' : 'Nova Simulação'} - Rescisão
                </S.BreadcrumbItemBold>
              </S.Breadcrumb>

              {/* Título Principal */}
              <S.MainTitle>{isEditMode ? 'Editar Simulação Rescisão' : 'Simulação Rescisão'}</S.MainTitle>

              {/* Info Banner */}
              <S.InfoBanner>
                <InfoIcon />
                <S.InfoText>
                  Os valores abaixo são estimativas para prévia e podem ser ajustados conforme política vigente.
                </S.InfoText>
              </S.InfoBanner>

              {/* Dados do colaborador */}
              <S.Section>
                <S.SectionHeader>Dados do(a) colaborador(a)</S.SectionHeader>

                <S.CollaboratorCard>
                  <S.CollaboratorInfo>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: '#6B7280',
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                    >
                      {(isEditMode && simulationToEdit?.employeeName
                        ? simulationToEdit.employeeName
                        : collaborator?.name)?.charAt(0)?.toUpperCase() || 'A'}
                    </Avatar>
                    <Box>
                      <S.CollaboratorName>
                        {isEditMode && simulationToEdit?.employeeName
                          ? simulationToEdit.employeeName
                          : collaborator?.name || ''}
                      </S.CollaboratorName>
                      <S.CollaboratorMatricula>
                        {isEditMode && simulationToEdit?.employeeRegistration
                          ? simulationToEdit.employeeRegistration
                          : collaborator?.matricula || ''}
                      </S.CollaboratorMatricula>
                    </Box>
                  </S.CollaboratorInfo>

                  <S.CollaboratorBadge>
                    {collaboratorDetails?.status ?? collaborator?.status ?? (isLoadingDetails ? 'Carregando...' : '—')}
                  </S.CollaboratorBadge>
                </S.CollaboratorCard>

                {/* Campos de dados do colaborador */}
                <S.FormFieldsGrid>
                  <Input
                    hasController={false}
                    name="nome"
                    label="Nome"
                    value={
                      isEditMode && simulationToEdit?.employeeName
                        ? simulationToEdit.employeeName
                        : collaborator?.name || ''
                    }
                    disabled
                  />

                  <DatePicker
                    hasController={false}
                    name="dataInicio"
                    label="Data Início"
                    value={
                      isEditMode && simulationToEdit?.startDate
                        ? formattedDatePtBr(simulationToEdit.startDate)
                        : collaborator?.dataInicio
                          ? formattedDatePtBr(collaborator.dataInicio)
                          : ''
                    }
                    disabled
                  />

                  <Input
                    hasController={false}
                    name="cargo"
                    label="Cargo"
                    value={
                      isEditMode && simulationToEdit?.position
                        ? simulationToEdit.position
                        : collaborator?.cargo || ''
                    }
                    disabled
                  />

                  <Input
                    hasController={false}
                    name="salario"
                    label="Salário"
                    value={
                      effectiveSalary > 0
                        ? currencyMask(effectiveSalary.toFixed(2))
                        : (isLoadingDetails ? 'Carregando...' : '')
                    }
                    disabled
                  />

                  <Input
                    hasController={false}
                    name="centroLucro"
                    label="Centro de Lucro"
                    value={collaborator?.centroLucro || filiData?.filiName || ''}
                    disabled
                  />
                </S.FormFieldsGrid>
              </S.Section>

              {/* Informações da Rescisão */}
              <S.Section>
                <S.SectionHeader>Informações da Rescisão</S.SectionHeader>

                <S.FormFieldsGrid>
                  <Select
                    name="tipoAviso"
                    label="Tipo Aviso Prévio"
                    placeholder="Selecione"
                    required
                    options={[
                      { value: 'TRABALHADO', label: 'Trabalhado' },
                      { value: 'INDENIZADO', label: 'Indenizado' }
                    ]}
                    disabled={!canEdit}
                  />

                  <DatePicker
                    name="dataRescisao"
                    label="Data Prevista Rescisão"
                    placeholder="00/00/0000"
                    required
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={!canEdit}
                  />

                  <S.DisabledFieldWrapper>
                    <S.FieldLabel>Tipo Rescisão</S.FieldLabel>
                    <S.DisabledField>
                      <S.DataValue>Sem Justa Causa</S.DataValue>
                    </S.DisabledField>
                  </S.DisabledFieldWrapper>
                </S.FormFieldsGrid>
              </S.Section>

              {/* Simulador de Rescisão */}
              {(tipoAviso && dataRescisao) && (
                <S.Section>
                  <S.SectionHeaderWithIcon>
                   <S.IconBackground>
                    <UserMinus size={20} color="#757575" />
                    </S.IconBackground>
                    <span>Simulador de Rescisão</span>
                  </S.SectionHeaderWithIcon>

                  {/* Salário e Encargos Card */}
                  <S.Card style={{ borderColor: 'rgba(40, 56, 151, 0.6)' }}>
                    {/* Salário */}
                    <S.CardSectionHeader>
                      <S.CardSectionIconBg style={{ backgroundColor: '#BFDBFE', color: '#1E40AF' }}>
                        <MoneyIcon />
                      </S.CardSectionIconBg>
                      <S.CardSectionTitle>Salário</S.CardSectionTitle>
                    </S.CardSectionHeader>

                    <S.CardItem style={{ paddingLeft: '60px' }}>
                      <S.CardItemLabel>
                        <S.LabelWithIcon>
                          Salário Proporcional
                          <S.TooltipWrapper>
                            <Tooltip
                              title="Cálculo: (Salário / 30) x Dias Trabalhados no Mês x 2"
                              placement="top"
                              width="fit-content"
                            >
                              <S.InfoIconContainer>
                                <InfoIcon />
                              </S.InfoIconContainer>
                            </Tooltip>
                          </S.TooltipWrapper>
                        </S.LabelWithIcon>
                        <S.CardItemCode style={{ textAlign: 'left' }}>64109301</S.CardItemCode>
                      </S.CardItemLabel>
                      <S.CardItemValue>
                        {isCalculatingSalary ? '...' : new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(financialSummary.salarioProporcional)}
                      </S.CardItemValue>
                      
                    </S.CardItem>

                    {/* Encargos */}
                    <S.CardSectionHeader>
                      <S.CardSectionIconBg style={{ color: '#6D7179' }}>
                        <BankIcon />
                      </S.CardSectionIconBg>
                      <S.CardSectionTitle>Encargos</S.CardSectionTitle>
                    </S.CardSectionHeader>

                    <S.CardItem style={{ paddingLeft: '60px' }}>
                      <S.CardItemLabel>
                        FGTS Art. 22
                        <S.CardItemCode style={{ textAlign: 'left' }}>64109301</S.CardItemCode>
                      </S.CardItemLabel>
                      <S.CardItemValueWithIcon>
                        {canEdit && (
                          <S.EditIconWrapper onClick={handleOpenEditFgtsModal}>
                            <PencilSimpleIcon size={16} weight="regular" />
                          </S.EditIconWrapper>
                        )}
                        <S.CardItemValue style={{ color: '#767676' }}>
                          {isCalculatingFgts
                            ? '...'
                            : new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }).format(financialSummary.fgts)}
                        </S.CardItemValue>
                      </S.CardItemValueWithIcon>
                    </S.CardItem>

                    <S.CardItem style={{ paddingLeft: '60px' }}>
                        <S.CardItemLabel>
                          Aviso Prévio
                          <S.CardItemSubtext>Anos completos: {`${Math.floor((completeMonthsFromApi ?? 0) / 12)},${(completeMonthsFromApi ?? 0) % 12}`} anos</S.CardItemSubtext>
                          <S.CardItemCode style={{ textAlign: 'left' }}>64109301</S.CardItemCode>
                        </S.CardItemLabel>
                        <S.CardItemValue>
                          {isCalculatingNoticePayment
                            ? '...'
                            : new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }).format(financialSummary.avisoPrevio)}
                        </S.CardItemValue>
                    </S.CardItem>
                  </S.Card>

                  {/* Resumo Financeiro da Rescisão */}
                  <S.SummaryCard style={{ borderColor: 'rgba(40, 56, 151, 0.6)', boxShadow: '4px 6px 10px -2px rgba(0, 0, 0, 0.28)' }}>
                    <S.CardSectionHeader>
                    <CalculatorIcon size={25} />
                      <S.CardSectionTitle>Resumo Financeiro da Rescisão</S.CardSectionTitle>
                      <S.SummaryUpdatedBadge>
                        <CheckCircleIcon size={14} weight="fill" />
                        Atualizado
                      </S.SummaryUpdatedBadge>
                    </S.CardSectionHeader>

                    <S.SummaryGrid>
                      <S.SummaryItem>
                        <S.SummaryItemLeft>
                          <S.CardSectionIconBg style={{ backgroundColor: '#BFDBFE', color: '#1E40AF' }}>
                            <MoneyIcon />
                          </S.CardSectionIconBg>
                          <S.SummaryLabel>Salário</S.SummaryLabel>
                        </S.SummaryItemLeft>
                        <S.SummaryValue>
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }).format(financialSummary.salarioProporcional)}
                        </S.SummaryValue>
                      </S.SummaryItem>

                      <S.SummaryItem>
                        <S.SummaryItemLeft>
                          <S.CardSectionIconBg style={{ color: '#6D7179' }}>
                          <BankIcon />
                          </S.CardSectionIconBg>
                          <S.SummaryLabel>Encargos</S.SummaryLabel>
                        </S.SummaryItemLeft>
                        <S.SummaryValue>
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }).format(financialSummary.fgts + financialSummary.avisoPrevio)}
                        </S.SummaryValue>
                      </S.SummaryItem>
                    </S.SummaryGrid>

                    <S.SummaryTotal>
                      <S.SummaryTotalLabel>Total Estimado</S.SummaryTotalLabel>
                      <S.SummaryTotalValue>
                        {(isCalculatingSalary || isCalculatingFgts || isCalculatingNoticePayment || isCalculatingTotalEstimated)
                          ? '...'
                          : new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }).format(estimatedTotal)}
                      </S.SummaryTotalValue>
                    </S.SummaryTotal>

                    {/* Salários e Ordenados (dedução) */}
                    <S.SummaryDeduction>
                      <S.SummaryDeductionLeft>
                        <MinusCircleIcon size={16} weight="fill" />
                        <S.SummaryDeductionLabel>Salário com Adicionais</S.SummaryDeductionLabel>
                      </S.SummaryDeductionLeft>
                      <S.SummaryDeductionValue>
                        - R$
                        <S.SummaryDeductionAmount>
                          {new Intl.NumberFormat('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }).format(salariosOrdenados)}
                        </S.SummaryDeductionAmount>
                      </S.SummaryDeductionValue>
                    </S.SummaryDeduction>

                    <S.SummaryWarning>
                      <WarningCircleIcon size={16} weight="fill" />
                      <S.SummaryWarningText>
                        Valor relativo ao Salário com adicionais já considerado no mês
                      </S.SummaryWarningText>
                    </S.SummaryWarning>

                    {/* Saldo */}
                    <S.BalanceCard>
                      <S.BalanceLeft>
                        <S.BalanceIconBg>
                          <AccountBalanceWallet />
                        </S.BalanceIconBg>
                        <Box>
                          <S.BalanceTitleRow>
                            <S.BalanceLabel>Saldo</S.BalanceLabel>
                            <S.TooltipWrapper>
                              <Tooltip
                                title="Saldo de rescisão com o abatimento do salário com adicionais no mês."
                                placement="top"
                                width="fit-content"
                              >
                                <S.InfoIconContainer>
                                  <InfoIcon />
                                </S.InfoIconContainer>
                              </Tooltip>
                            </S.TooltipWrapper>
                          </S.BalanceTitleRow>
                          <S.BalanceSubtext>(Total Rescisão - Salário com adicionais)</S.BalanceSubtext>
                        </Box>
                      </S.BalanceLeft>
                      <S.BalanceValue>
                        {(isCalculatingSalary || isCalculatingFgts || isCalculatingNoticePayment || isCalculatingTotalEstimated)
                          ? '...'
                          : new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }).format(saldoRescisao)}
                      </S.BalanceValue>
                    </S.BalanceCard>
                  </S.SummaryCard>
                </S.Section>
              )}
            </S.FormContainer>
          </form>
        </FormProvider>
      </BaseModal2>

      {/* Modal de alerta de alterações não salvas (RF14) */}
      <ReturnModal
        open={openCancelModal}
        headerIcon={<WarningIcon />}
        headerTitle="Alterações não salvas"
        message="Alterações não salvas. Ao confirmar, os dados inseridos não serão salvos."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        isLoading={isCancelModalConfirming}
        onClose={setOpenCancelModal}
        onConfirm={() => {
          if (cancelConfirmLockRef.current) return
          cancelConfirmLockRef.current = true
          setIsCancelModalConfirming(true)
          setOpenCancelModal(false)
          methods.reset(initialFormValues)
          onClose(false)
        }}
      />

      {/* Modal de edição do FGTS Art. 22 */}
      <EditFgtsModal
        open={openEditFgtsModal}
        onClose={setOpenEditFgtsModal}
        currentValue={financialSummary.fgts}
        onConfirm={async (value: number) => {
          if (!canEdit) return Promise.resolve()

          // Atualizar o valor do FGTS no formulário
          methods.setValue('fgtsEditavel', String(value))
          setOpenEditFgtsModal(false)
          return Promise.resolve()
        }}
      />
    </>
  )
}
